<template>
  <div
    v-if="actions && actions.length"
    ref="wrapperRef"
    class="visitor-row-action"
    @keydown.escape.stop="closeMenu"
  >
    <IconButton
      ref="triggerRef"
      icon="ri-more-2-fill"
      :aria-label="toggleAriaLabel"
      :aria-expanded="state.openModal ? 'true' : 'false'"
      aria-haspopup="menu"
      :aria-controls="menuId"
      class="visitor-row-action-icon-button"
      :class="{ active: state.openModal }"
      @click.stop="toggleMenu"
    />
    <Teleport to="body">
      <div
        v-if="state.openModal"
        class="visitor-row-action-backdrop"
        aria-hidden="true"
        @mousedown.prevent="closeMenu"
      />
      <div
        v-if="state.openModal"
        :id="menuId"
        ref="menuRef"
        class="visitor-row-action-dropdown-panel"
        role="menu"
        :aria-label="$t('common.table_row_actions_menu')"
        :style="menuFixedStyle || undefined"
        @keydown.stop="onMenuKeydown"
      >
        <button
          v-for="action in actions"
          :key="action.id"
          type="button"
          role="menuitem"
          tabindex="-1"
          class="visitor-row-action-item"
          @click.stop="onClickAction(action)"
        >
          <div class="visitor-row-action-icon">
            <i :class="action.icon" aria-hidden="true"></i>
          </div>
          <div class="visitor-row-action-title">
            <span>{{ action.label }}</span>
          </div>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  reactive,
  ref,
  watch,
  nextTick,
  onUnmounted,
  computed,
  getCurrentInstance
} from "vue"
import { useI18n } from "vue-i18n"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import { focusWithoutScroll } from "@/helpers/focusWithoutScroll.js"

const MENU_MARGIN_PX = 8

export default {
  name: "VisitorRowAction",
  components: { IconButton },
  props: {
    actions: {
      type: Array,
      required: true
    }
  },
  emits: ["clickAction"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const instance = getCurrentInstance()
    const menuId = `visitor-row-actions-${instance?.uid ?? Math.random().toString(36).slice(2, 9)}`

    const wrapperRef = ref(null)
    const triggerRef = ref(null)
    const menuRef = ref(null)
    const menuFixedStyle = ref(null)

    const state = reactive({
      openModal: false
    })

    const toggleAriaLabel = computed(() =>
      state.openModal
        ? t("common.table_row_actions_close_menu")
        : t("common.table_row_actions_open_menu")
    )

    let repositionRaf = null
    const scheduleReposition = () => {
      if (!state.openModal) return
      if (repositionRaf != null) cancelAnimationFrame(repositionRaf)
      repositionRaf = requestAnimationFrame(() => {
        repositionRaf = null
        updateMenuPosition()
      })
    }

    const getTriggerEl = () => {
      const inst = triggerRef.value
      return inst?.$el ?? inst
    }

    /** Prefer below the ⋮ control, right-aligned; flip above if needed — fixed + teleport avoids table overflow clipping. */
    const updateMenuPosition = () => {
      if (!state.openModal) return
      nextTick(() => {
        requestAnimationFrame(() => {
          if (!state.openModal) return
          const menu = menuRef.value
          const triggerEl = getTriggerEl()
          if (!menu || !triggerEl) return

          const margin = MENU_MARGIN_PX
          const vw =
            document.documentElement?.clientWidth ?? window.innerWidth ?? 0
          const vh =
            document.documentElement?.clientHeight ?? window.innerHeight ?? 0
          const tr = triggerEl.getBoundingClientRect()
          const mw = menu.offsetWidth || menu.getBoundingClientRect().width
          const mh = menu.offsetHeight || menu.getBoundingClientRect().height
          const gap = 4

          let left = tr.right - mw
          let top = tr.bottom + gap

          if (left < margin) left = margin
          if (left + mw > vw - margin) {
            left = Math.max(margin, vw - margin - mw)
          }

          if (top + mh > vh - margin) {
            top = tr.top - mh - gap
          }
          if (top < margin) {
            top = margin
          }

          menuFixedStyle.value = {
            position: "fixed",
            left: `${Math.round(left)}px`,
            top: `${Math.round(top)}px`,
            transform: "none",
            right: "auto",
            bottom: "auto",
            zIndex: 100040
          }
        })
      })
    }

    const closeMenu = () => {
      state.openModal = false
      menuFixedStyle.value = null
    }

    const toggleMenu = () => {
      state.openModal = !state.openModal
      if (!state.openModal) {
        menuFixedStyle.value = null
      }
    }

    const onClickAction = (action) => {
      emit("clickAction", action)
      closeMenu()
    }

    const onDocMouseDown = (event) => {
      if (!state.openModal) return
      const el = event?.target
      if (wrapperRef.value?.contains(el)) return
      if (menuRef.value?.contains(el)) return
      closeMenu()
    }

    const onMenuKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault()
        closeMenu()
        focusWithoutScroll(getTriggerEl())
      }
    }

    watch(
      () => state.openModal,
      (open) => {
        if (open) {
          document.addEventListener("mousedown", onDocMouseDown, true)
          window.addEventListener("resize", scheduleReposition)
          window.addEventListener("scroll", scheduleReposition, true)
          void nextTick(() => {
            void nextTick(() => {
              updateMenuPosition()
              focusWithoutScroll(menuRef.value?.querySelector("button") ?? null)
            })
          })
        } else {
          document.removeEventListener("mousedown", onDocMouseDown, true)
          window.removeEventListener("resize", scheduleReposition)
          window.removeEventListener("scroll", scheduleReposition, true)
          if (repositionRaf != null) {
            cancelAnimationFrame(repositionRaf)
            repositionRaf = null
          }
        }
      }
    )

    onUnmounted(() => {
      document.removeEventListener("mousedown", onDocMouseDown, true)
      window.removeEventListener("resize", scheduleReposition)
      window.removeEventListener("scroll", scheduleReposition, true)
      if (repositionRaf != null) {
        cancelAnimationFrame(repositionRaf)
      }
    })

    return {
      menuId,
      state,
      wrapperRef,
      triggerRef,
      menuRef,
      menuFixedStyle,
      toggleAriaLabel,
      toggleMenu,
      closeMenu,
      onClickAction,
      onMenuKeydown
    }
  }
}
</script>
